import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import type { PurchaseProduct } from '@puzzlemania/core';
import { IAP_PRODUCT_IDS, WEB_IAP_PRODUCT_IDS, iapSourceIdForProduct } from '@puzzlemania/economy';
import {
  FEATURE_FLAG_KEYS,
  REMOTE_CONFIG_KEYS,
  isInRollout,
  remoteConfigStore,
} from '@puzzlemania/remote-config';
import { ShopPopup, type ShopPopupPack } from '@puzzlemania/ui';
import { coinIconUrl } from '../assets';
import { usePlatformServices } from '../composition/PlatformServicesContext';
import { useShopPopupStore } from '../state/shopPopupStore';

interface PackDefinition {
  productId: string;
  nameKey: string;
  coins: number;
  gems: number;
}

// Play Console (Android) and RevenueCat Web Billing (Stripe) are separate catalogs with
// their own product ids — same condition resolvePurchaseProvider() uses to pick a
// provider, so the ids requested here always match whichever provider actually resolved.
const ACTIVE_PRODUCT_IDS =
  Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android'
    ? IAP_PRODUCT_IDS
    : WEB_IAP_PRODUCT_IDS;

/**
 * Module 28 — the single app-level owner of the shared ShopPopup: mounted once
 * in App.tsx (beside the router, so it overlays the Lobby and any running game
 * alike) and opened via useShopPopupStore from the Lobby header or — through
 * the composition root's IGameContext bridge — from any game's HUD. Owns
 * everything the retired ShopScreen owned: product loading, RC gating,
 * purchase execution, crediting, error handling, and analytics.
 */
export function ShopPopupContainer() {
  const { t } = useTranslation();
  const platform = usePlatformServices();
  const isOpen = useShopPopupStore((s) => s.isOpen);
  const from = useShopPopupStore((s) => s.from);
  const close = useShopPopupStore((s) => s.close);
  const [products, setProducts] = useState<PurchaseProduct[] | null>(null);
  const [purchasingId, setPurchasingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const loggedViewedRef = useRef(false);

  // Reactive to a live RC refresh, matching GameGrid's/AppLayout's own flag-check pattern.
  const liveFlags = remoteConfigStore((s) => s.flags);
  const iapEnabled =
    liveFlags[FEATURE_FLAG_KEYS.IAP_ENABLED] ??
    platform.remoteConfig.getBoolean(FEATURE_FLAG_KEYS.IAP_ENABLED, true);
  const rolloutPercentage = platform.remoteConfig.getNumber(
    REMOTE_CONFIG_KEYS.IAP_ROLLOUT_PERCENTAGE,
    100,
  );
  const isGated = !iapEnabled || !isInRollout(platform.uid, rolloutPercentage);

  const packs: PackDefinition[] = [
    {
      productId: ACTIVE_PRODUCT_IDS.SMALL,
      nameKey: 'shop.pack.small.name',
      coins: platform.remoteConfig.getNumber(REMOTE_CONFIG_KEYS.IAP_PACK_SMALL_COINS, 5000),
      gems: 0,
    },
    {
      productId: ACTIVE_PRODUCT_IDS.MEDIUM,
      nameKey: 'shop.pack.medium.name',
      coins: platform.remoteConfig.getNumber(REMOTE_CONFIG_KEYS.IAP_PACK_MEDIUM_COINS, 25000),
      gems: platform.remoteConfig.getNumber(REMOTE_CONFIG_KEYS.IAP_PACK_MEDIUM_GEMS, 10),
    },
    {
      productId: ACTIVE_PRODUCT_IDS.LARGE,
      nameKey: 'shop.pack.large.name',
      coins: platform.remoteConfig.getNumber(REMOTE_CONFIG_KEYS.IAP_PACK_LARGE_COINS, 60000),
      gems: platform.remoteConfig.getNumber(REMOTE_CONFIG_KEYS.IAP_PACK_LARGE_GEMS, 30),
    },
  ];

  useEffect(() => {
    if (!isOpen) return;
    platform.providers.analytics.logEvent('platform_shop_opened', { from });
  }, [isOpen, from, platform.providers.analytics]);

  useEffect(() => {
    // Products load on first open (not app boot) and stay cached for later opens.
    if (!isOpen || isGated || products !== null) return;
    let cancelled = false;
    platform.providers.purchase
      .getProducts(packs.map((p) => p.productId))
      .then((result) => {
        if (cancelled) return;
        setProducts(result);
        if (!loggedViewedRef.current) {
          loggedViewedRef.current = true;
          for (const product of result) {
            platform.providers.analytics.logEvent('product_viewed', {
              product_id: product.productId,
              price: product.priceString,
            });
          }
        }
      })
      .catch(() => {
        // A rejected getProducts() (e.g. the RevenueCat Web Billing script blocked by an
        // ad-blocker or a CSP gap) must not leave the popup stuck on a perpetual
        // loading state with no visible error.
        if (!cancelled) setError(t('shop.purchaseFailed'));
      });
    return () => {
      cancelled = true;
    };
    // packs is recomputed fresh every render from stable remote-config reads — keying
    // this effect on isOpen/isGated/products (plus the stable provider refs) is
    // sufficient without needing packs itself as a dependency.
  }, [isOpen, isGated, products, platform.providers.purchase, platform.providers.analytics, t]);

  async function handleBuy(productId: string) {
    const pack = packs.find((p) => p.productId === productId);
    const product = products?.find((p) => p.productId === productId);
    if (!pack || !product) return;
    setPurchasingId(productId);
    setError(null);
    try {
      await platform.providers.purchase.purchase(productId);
      const sourceId = iapSourceIdForProduct(productId);
      platform.economy.credit('coins', pack.coins, sourceId);
      if (pack.gems > 0) {
        platform.economy.credit('gems', pack.gems, sourceId);
      }
      // Real ISO currency when the provider can supply one (RevenueCat Web Billing);
      // Android's purchases-capacitor has no real currency source, so this still falls
      // back to the USD placeholder for that path.
      platform.providers.analytics.logEvent('product_purchased', {
        product_id: productId,
        price: product.priceString,
        currency: product.currency ?? 'USD',
      });
    } catch {
      setError(t('shop.purchaseFailed'));
    } finally {
      setPurchasingId(null);
    }
  }

  const popupPacks: ShopPopupPack[] = packs.map((pack) => {
    const product = products?.find((p) => p.productId === pack.productId);
    return { ...pack, ...(product?.priceString ? { priceString: product.priceString } : {}) };
  });

  return (
    <ShopPopup
      isOpen={isOpen}
      onClose={close}
      packs={popupPacks}
      isLoading={products === null}
      purchasingId={purchasingId}
      errorMessage={error}
      comingSoon={isGated}
      coinIconUrl={coinIconUrl}
      onBuy={(productId) => void handleBuy(productId)}
    />
  );
}
